import {
  Users, Wallet, Coins, Clock, BedDouble, Ticket, UtensilsCrossed, Plane,
  Shirt, Luggage, ShoppingBag, PartyPopper, ListChecks, Contact,
} from "lucide-react";

// Each section config powers BachSection via BachManager.
export const SECTIONS = {
  guests: {
    entity: "guests", title: "Guest List", subtitle: "Everyone celebrating the bride.", icon: Users,
    fields: [
      { key: "name", label: "Name", full: true },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "rsvp", label: "RSVP", type: "select", options: ["Attending", "Maybe", "Not Attending", "Awaiting"] },
      { key: "dietary", label: "Dietary" },
      { key: "emergency", label: "Emergency info" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "name", label: "Name" }, { key: "rsvp", label: "RSVP" }, { key: "phone", label: "Phone" }, { key: "dietary", label: "Dietary" }],
  },
  budget: {
    entity: "budget_categories", title: "Budget", subtitle: "Estimated vs actual, deposits and deadlines.", icon: Wallet,
    fields: [
      { key: "name", label: "Category", full: true },
      { key: "planned", label: "Estimated cost", type: "number" },
      { key: "actual", label: "Actual cost", type: "number" },
      { key: "paid", label: "Paid", type: "number" },
      { key: "deposit", label: "Deposit", type: "number" },
      { key: "deadline", label: "Payment deadline", type: "date" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "name", label: "Category" }, { key: "planned", label: "Estimated", money: true }, { key: "actual", label: "Actual", money: true }, { key: "deadline", label: "Deadline" }],
    summary: (items) => {
      const est = items.reduce((s, i) => s + (Number(i.planned) || 0), 0);
      const act = items.reduce((s, i) => s + (Number(i.actual) || 0), 0);
      return [{ label: "Estimated", value: est, money: true }, { label: "Actual", value: act, money: true }, { label: "Remaining", value: est - act, money: true }];
    },
  },
  payments: {
    entity: "payments", title: "Payment Tracker", subtitle: "Who owes what for the group trip.", icon: Coins,
    fields: [
      { key: "person", label: "Person", full: true },
      { key: "amount_owed", label: "Amount owed", type: "number" },
      { key: "amount_paid", label: "Amount paid", type: "number" },
      { key: "due_date", label: "Due date", type: "date" },
      { key: "status", label: "Status", type: "select", options: ["Unpaid", "Partial", "Paid"] },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "person", label: "Person" }, { key: "amount_owed", label: "Owed", money: true }, { key: "amount_paid", label: "Paid", money: true }, { key: "remaining", label: "Remaining", money: true, compute: (i) => (Number(i.amount_owed) || 0) - (Number(i.amount_paid) || 0) }, { key: "status", label: "Status", badge: "payment" }],
    summary: (items) => {
      const owed = items.reduce((s, i) => s + (Number(i.amount_owed) || 0), 0);
      const paid = items.reduce((s, i) => s + (Number(i.amount_paid) || 0), 0);
      return [{ label: "Total group cost", value: owed, money: true }, { label: "Total collected", value: paid, money: true }, { label: "Still outstanding", value: owed - paid, money: true }];
    },
  },
  itinerary: {
    entity: "itinerary", title: "Itinerary", subtitle: "Plan every day, hour by hour.", icon: Clock,
    fields: [
      { key: "day", label: "Day", full: true },
      { key: "date", label: "Date", type: "date" },
      { key: "time", label: "Time" },
      { key: "activity", label: "Activity", full: true },
      { key: "location", label: "Location" },
      { key: "reference", label: "Booking reference" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "day", label: "Day" }, { key: "time", label: "Time" }, { key: "activity", label: "Activity" }, { key: "location", label: "Location" }],
  },
  accommodation: {
    entity: "accommodation", title: "Accommodation", subtitle: "Where the party is staying.", icon: BedDouble,
    fields: [
      { key: "name", label: "Property", full: true },
      { key: "address", label: "Address", full: true },
      { key: "check_in", label: "Check-in", type: "date" },
      { key: "check_out", label: "Check-out", type: "date" },
      { key: "reference", label: "Booking reference" },
      { key: "cost", label: "Cost", type: "number" },
      { key: "who", label: "Who's staying" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "name", label: "Property" }, { key: "check_in", label: "Check-in" }, { key: "check_out", label: "Check-out" }, { key: "cost", label: "Cost", money: true }],
  },
  activities: {
    entity: "activities", title: "Activities & Bookings", subtitle: "Experiences, deposits and references.", icon: Ticket,
    fields: [
      { key: "activity", label: "Activity", full: true },
      { key: "date", label: "Date", type: "date" },
      { key: "time", label: "Time" },
      { key: "location", label: "Location" },
      { key: "cost", label: "Cost", type: "number" },
      { key: "deposit", label: "Deposit", type: "number" },
      { key: "remaining", label: "Remaining", type: "number" },
      { key: "reference", label: "Booking reference" },
      { key: "contact", label: "Contact" },
      { key: "status", label: "Status", type: "select", options: ["Idea", "Planned", "Booked", "Paid"] },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "activity", label: "Activity" }, { key: "date", label: "Date" }, { key: "cost", label: "Cost", money: true }, { key: "status", label: "Status" }],
  },
  food: {
    entity: "food", title: "Food & Drinks", subtitle: "Restaurants, brunch and bars.", icon: UtensilsCrossed,
    fields: [
      { key: "name", label: "Place", full: true },
      { key: "type", label: "Type", type: "select", options: ["Restaurant", "Brunch", "Bar", "Cafe", "Other"] },
      { key: "date", label: "Date", type: "date" },
      { key: "time", label: "Time" },
      { key: "address", label: "Address", full: true },
      { key: "reference", label: "Reservation reference" },
      { key: "cost", label: "Cost / budget", type: "number" },
      { key: "dietary", label: "Dietary notes" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "name", label: "Place" }, { key: "type", label: "Type" }, { key: "date", label: "Date" }, { key: "cost", label: "Cost", money: true }],
  },
  transport: {
    entity: "transport", title: "Transport", subtitle: "Flights, transfers and taxis.", icon: Plane,
    fields: [
      { key: "type", label: "Type", type: "select", options: ["Flight", "Train", "Transfer", "Taxi / Uber", "Car hire", "Other"] },
      { key: "details", label: "Details", full: true },
      { key: "departure", label: "Departure" },
      { key: "arrival", label: "Arrival" },
      { key: "reference", label: "Booking reference" },
      { key: "cost", label: "Cost", type: "number" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "type", label: "Type" }, { key: "details", label: "Details" }, { key: "departure", label: "Departure" }, { key: "cost", label: "Cost", money: true }],
  },
  outfits: {
    entity: "outfits", title: "Outfits", subtitle: "Plan a look for every moment.", icon: Shirt,
    fields: [
      { key: "event", label: "Event", type: "select", options: ["Travel", "Welcome dinner", "Main night", "Brunch", "Pool / beach", "Other"] },
      { key: "description", label: "Outfit", full: true },
      { key: "image", label: "Inspiration image", full: true, type: "image" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "event", label: "Event" }, { key: "description", label: "Outfit" }],
    image: "image",
  },
  packing: {
    entity: "packing", title: "Packing List", subtitle: "Tick items off as you pack.", icon: Luggage,
    variant: "checklist", doneKey: "done", labelKey: "item",
    fields: [
      { key: "item", label: "Item", full: true },
      { key: "category", label: "Category", type: "select", options: ["Clothing", "Shoes", "Beauty", "Toiletries", "Accessories", "Travel documents", "Decorations", "Party items", "Other"] },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
  },
  shopping: {
    entity: "shopping", title: "Shopping List", subtitle: "Everything to buy for the celebration.", icon: ShoppingBag,
    fields: [
      { key: "item", label: "Item", full: true },
      { key: "quantity", label: "Quantity", type: "number" },
      { key: "est_price", label: "Estimated price", type: "number" },
      { key: "actual_price", label: "Actual price", type: "number" },
      { key: "purchased", label: "Purchased", type: "checkbox" },
      { key: "who", label: "Responsible" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "item", label: "Item" }, { key: "quantity", label: "Qty" }, { key: "est_price", label: "Est.", money: true }, { key: "purchased", label: "Bought", bool: true }],
  },
  games: {
    entity: "games", title: "Games & Activities", subtitle: "Save the party games and who's running them.", icon: PartyPopper,
    fields: [
      { key: "name", label: "Game", full: true },
      { key: "instructions", label: "Instructions", type: "textarea", full: true },
      { key: "needed", label: "What's needed", full: true },
      { key: "who", label: "Responsible" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "name", label: "Game" }, { key: "who", label: "Responsible" }],
  },
  checklist: {
    entity: "tasks", title: "Task Checklist", subtitle: "Everything to organise before the trip.", icon: ListChecks,
    variant: "checklist", doneKey: "done", labelKey: "title",
    fields: [
      { key: "title", label: "Task", full: true },
      { key: "category", label: "Category" },
      { key: "due_date", label: "Due date", type: "date" },
      { key: "assignee", label: "Assigned to" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
  },
  contacts: {
    entity: "contacts", title: "Contacts", subtitle: "Useful and emergency contacts.", icon: Contact,
    fields: [
      { key: "name", label: "Name", full: true },
      { key: "role", label: "Type", type: "select", options: ["Guest", "Accommodation", "Restaurant", "Activity provider", "Transport", "Emergency", "Other"] },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
    columns: [{ key: "name", label: "Name" }, { key: "role", label: "Type" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }],
  },
};

export const BACH_NAV = [
  { section: "Plan", items: [
    { to: "/bach", label: "Dashboard", key: "dashboard", end: true },
    { to: "/bach/setup", label: "Setup", key: "setup" },
    { to: "/bach/checklist", label: "Checklist", key: "checklist" },
  ]},
  { section: "People & Money", items: [
    { to: "/bach/guests", label: "Guest List", key: "guests" },
    { to: "/bach/budget", label: "Budget", key: "budget" },
    { to: "/bach/payments", label: "Payment Tracker", key: "payments" },
  ]},
  { section: "The Trip", items: [
    { to: "/bach/itinerary", label: "Itinerary", key: "itinerary" },
    { to: "/bach/accommodation", label: "Accommodation", key: "accommodation" },
    { to: "/bach/activities", label: "Activities", key: "activities" },
    { to: "/bach/food", label: "Food & Drinks", key: "food" },
    { to: "/bach/transport", label: "Transport", key: "transport" },
  ]},
  { section: "The Vibe", items: [
    { to: "/bach/theme", label: "Theme & Inspiration", key: "theme" },
    { to: "/bach/outfits", label: "Outfits", key: "outfits" },
    { to: "/bach/packing", label: "Packing", key: "packing" },
    { to: "/bach/shopping", label: "Shopping", key: "shopping" },
    { to: "/bach/games", label: "Games", key: "games" },
  ]},
  { section: "More", items: [
    { to: "/bach/contacts", label: "Contacts", key: "contacts" },
    { to: "/bach/notes", label: "Notes", key: "notes" },
  ]},
];
